import { PassingStatusCategory } from 'pdftron/utils/barcodeGradeColors';

export interface GradeIconProps {
  color: string;
  size?: number;
  marginRight?: string;
}

export const iconDefaults = {
  size: 14,
  marginRight: '6px',
};

export const FailIcon = ({
  color,
  size = iconDefaults.size,
  marginRight = iconDefaults.marginRight,
}: GradeIconProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ marginRight, flexShrink: 0 }}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.99984 1.16663C3.77984 1.16663 1.1665 3.77996 1.1665 6.99996C1.1665 10.22 3.77984 12.8333 6.99984 12.8333C10.2198 12.8333 12.8332 10.22 12.8332 6.99996C12.8332 3.77996 10.2198 1.16663 6.99984 1.16663ZM7.58317 9.91663H6.4165V8.74996H7.58317V9.91663ZM7.58317 7.58329H6.4165V4.08329H7.58317V7.58329Z"
      fill={color}
    />
  </svg>
);

// Helper function to extract status and description from status format: "Pass (description)"
export const extractStatusFromValue = (value: string): { status: string; description: string } | null => {
  if (!value) return null;
  const statusPattern = /^(\w+)\s*\((.+)\)$/;
  const match = value.match(statusPattern);
  if (match) {
    return {
      status: match[1], // "Pass", "Fail", or "Warning"
      description: match[2], // Text in brackets
    };
  }
  return null;
};

// Helper function to map status word to category for icon display
export const getStatusCategory = (status: string): 'pass' | 'warning' | 'fail' => {
  const statusLower = status.toLowerCase();
  if (statusLower === 'pass') return 'pass';
  if (statusLower === 'warning') return 'warning';
  if (statusLower === 'fail') return 'fail';
  return 'pass'; // default to pass
};

export interface BarcodeStatusResult {
  category: PassingStatusCategory | null;
  displayValue: string;
  statusDescription: string;
}

// Type for parsed status JSON from backend
type ParsedStatusInfo = {
  status: string;
  displayString: string;
  statusDescription: string;
};

/**
 * Parses a humanReadableStatus / resolvedUrlStatus field (JSON string
 * `{status, displayString, statusDescription}`) into the pieces a barcode row needs: the
 * pass/warning/fail category, the value to display, and the explanatory message.
 *
 * These opt-in rows follow the same pattern as every other parameter row — the message is
 * surfaced as the inline expandable note, and the status icon is placed
 * on the left next to the label by the row renderer.
 */
const parseBarcodeStatus = (value: string): BarcodeStatusResult => {
  if (!value || value === 'N/A') {
    return { category: null, displayValue: value, statusDescription: '' };
  }

  let statusInfo: ParsedStatusInfo;
  try {
    statusInfo = JSON.parse(value);
  } catch {
    // Fallback for old format or invalid JSON
    return { category: null, displayValue: value, statusDescription: '' };
  }

  // Only displayString present (e.g. { "displayString": "N/A" }) — no status, no message.
  if (!statusInfo.status) {
    return { category: null, displayValue: statusInfo.displayString || 'N/A', statusDescription: '' };
  }

  if (!statusInfo.displayString) {
    return { category: null, displayValue: value, statusDescription: '' };
  }

  return {
    category: getStatusCategory(statusInfo.status),
    displayValue: statusInfo.displayString,
    statusDescription: statusInfo.statusDescription || '',
  };
};

export const processHumanReadableStatus = parseBarcodeStatus;
export const processResolvedUrlStatus = parseBarcodeStatus;
